import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { reclassifyLayer } from "../../api/projects";
import { useDispatchExtraction } from "../../hooks/useComponents";
import {
  projectsQueryKeys,
  useAddToSitePlanCandidates,
  useLayers,
  useProject,
  useRemoveSitePlanCandidate,
  useSitePlanCandidates,
} from "../../hooks/useProjects";
import type {
  Classification,
  Layer,
  ReclassifyResponseBody,
} from "../../types/projects";
import {
  ClassificationBadge,
  GeometryBadge,
} from "../components-step/ComponentBadge";
import { DeleteFileModal } from "./DeleteFileModal";
import { FilePreviewPanel } from "./FilePreviewPanel";
import { ReclassifyHintModal } from "./ReclassifyHintModal";
import {
  computeFileRowActions,
  type FileAction,
} from "./fileRowState";
import {
  FileUploadModal,
  type ModalFileStatus,
  type ModalFileUpdate,
} from "../upload/FileUploadModal";

interface Props {
  projectId: string;
}

interface PendingClassify {
  snapshot: Classification | null;
  startedAt: number;
}

interface UploadInfo {
  status: ModalFileStatus;
  progress: number;
  errorMessage?: string | null;
}

/** Stage 8.7 — Name (alphabetic) and Uploaded (chronological) are
 * sortable; Kind is categorical and intentionally NOT sortable. */
type SortKey = "name" | "uploaded";
type SortDirection = "asc" | "desc";

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 60_000;
const COMPACT_BREAKPOINT_PX = 1280;

function useIsCompactViewport(): boolean {
  const [isCompact, setIsCompact] = useState(
    () =>
      typeof window !== "undefined" &&
      window.innerWidth < COMPACT_BREAKPOINT_PX,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () =>
      setIsCompact(window.innerWidth < COMPACT_BREAKPOINT_PX);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isCompact;
}

/**
 * Middle-ellipsis filename truncation: keeps the extension visible at
 * the end so the user can tell PDF / KMZ / GeoJSON apart at a glance.
 * Below ``maxLen`` chars the raw filename is returned. Returns the
 * pair (displayed, original) so the row can put the original in the
 * ``title`` tooltip.
 */
function middleEllipsis(name: string, maxLen: number = 38): string {
  if (name.length <= maxLen) return name;
  const ext = name.lastIndexOf(".");
  // No extension → trailing ellipsis is fine.
  if (ext < 0 || ext > name.length - 2) {
    return `${name.slice(0, maxLen - 1)}…`;
  }
  const extPart = name.slice(ext); // includes leading dot
  const left = Math.max(6, maxLen - extPart.length - 1);
  return `${name.slice(0, left)}…${extPart}`;
}

/**
 * Compact "uploaded at" formatter for the 160px column. Locale-aware,
 * shows both date and time-to-the-minute so the user can read the
 * sort order at a glance (e.g. ``5/14/26, 10:23 AM``). The full
 * timestamp lives in the cell's ``title`` tooltip.
 */
function formatUploadedAt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Sort arrow renderer with the Stage 8.7 dim-until-hover behavior.
 * The active sort gets a strong arrow; inactive columns get a dim
 * down-arrow as a "this is sortable" hint.
 */
function SortArrow({
  active,
  direction,
}: {
  active: boolean;
  direction: SortDirection;
}) {
  return (
    <span
      aria-hidden="true"
      style={{
        marginLeft: 6,
        color: active ? "#475569" : "#cbd5e1",
        fontWeight: 500,
        fontSize: "0.85em",
      }}
    >
      {active ? (direction === "asc" ? "↑" : "↓") : "↓"}
    </span>
  );
}

/**
 * Kind-column renderer. Order of precedence:
 *
 * 1. File is uploading (browser → S3) → spinner + "Uploading X%"
 * 2. File is in the confirm step (server-side parse) → "Processing…"
 * 3. PDF classify task is in flight → spinner + "Analyzing…"
 * 4. Otherwise → the classification / geometry badge
 *
 * Per Stage 8.6 Item 4: the row must NEVER be missing a Kind value
 * while the file is in flight.
 */
function KindCell({
  layer,
  upload,
  pendingClassify,
  classifySystemError,
}: {
  layer: Layer;
  upload: UploadInfo | undefined;
  pendingClassify: boolean;
  classifySystemError: boolean;
}) {
  const filename = (layer.original_filename || layer.name || "").toLowerCase();
  const isPdf = filename.endsWith(".pdf");
  const isGeom =
    filename.endsWith(".kmz") ||
    filename.endsWith(".kml") ||
    filename.endsWith(".geojson") ||
    filename.endsWith(".json");

  if (upload) {
    if (upload.status === "failed") {
      return (
        <span
          data-testid={`kind-upload-failed-${layer.id}`}
          style={errorBadge}
          title={upload.errorMessage ?? "Upload failed"}
        >
          ⚠️ Upload failed
        </span>
      );
    }
    if (upload.status === "uploading") {
      const pct = Math.round(upload.progress * 100);
      return (
        <span data-testid={`kind-uploading-${layer.id}`} style={inflightBadge}>
          <Spinner /> Uploading… {pct}%
        </span>
      );
    }
    if (upload.status === "presigning") {
      return (
        <span data-testid={`kind-presigning-${layer.id}`} style={inflightBadge}>
          <Spinner /> Preparing…
        </span>
      );
    }
    if (upload.status === "confirming") {
      return (
        <span data-testid={`kind-processing-${layer.id}`} style={inflightBadge}>
          <Spinner /> Processing…
        </span>
      );
    }
  }

  const classifying =
    pendingClassify || (isPdf && layer.classification === null);
  if (classifying) {
    return (
      <span data-testid={`kind-analyzing-${layer.id}`} style={inflightBadge}>
        <Spinner /> Analyzing…
      </span>
    );
  }

  if (classifySystemError) {
    return (
      <span data-testid={`kind-classify-failed-${layer.id}`} style={errorBadge}>
        ⚠️ Classification failed
      </span>
    );
  }

  if (isPdf) {
    return (
      <ClassificationBadge
        classification={layer.classification}
        confidence={layer.classification_confidence}
      />
    );
  }
  if (isGeom) {
    return (
      <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
        <GeometryBadge layerType={layer.layer_type} />
        <span style={{ color: "#475569", fontSize: "0.8rem" }}>
          {layer.feature_count}{" "}
          {layer.feature_count === 1 ? "feature" : "features"}
        </span>
      </span>
    );
  }
  return <span style={{ color: "#475569" }}>📎 Other</span>;
}

function TrashIcon() {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        marginRight: 4,
        border: "2px solid #c7d2fe",
        borderTopColor: "#4338ca",
        borderRadius: "50%",
        animation: "spark-spin 0.8s linear infinite",
        verticalAlign: "middle",
      }}
    />
  );
}

export function ProjectFilesTab({ projectId }: Props) {
  const navigate = useNavigate();
  const isCompact = useIsCompactViewport();
  const [showUpload, setShowUpload] = useState(false);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [hoveredLayerId, setHoveredLayerId] = useState<string | null>(null);
  const [hintForLayer, setHintForLayer] = useState<Layer | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Layer | null>(null);
  const [pending, setPending] = useState<Map<string, PendingClassify>>(
    () => new Map(),
  );
  const [systemErrors, setSystemErrors] = useState<Set<string>>(
    () => new Set(),
  );
  const [uploads, setUploads] = useState<Map<string, UploadInfo>>(
    () => new Map(),
  );

  const onUploadProgress = useCallback((update: ModalFileUpdate) => {
    if (!update.layerId) return;
    const layerId = update.layerId;
    if (update.status === "uploaded") {
      setUploads((prev) => {
        const next = new Map(prev);
        next.delete(layerId);
        return next;
      });
      // The upload is done but a background classifier is still running
      // (PDF → ``classification``, KMZ/KML/GeoJSON → ``feature_classifications``).
      // Track the fresh layer as pending so the list keeps polling and the
      // UI updates itself when the result lands — no manual page refresh.
      setPending((prev) => {
        if (prev.has(layerId)) return prev;
        const next = new Map(prev);
        next.set(layerId, { snapshot: null, startedAt: Date.now() });
        return next;
      });
    } else {
      setUploads((prev) => {
        const next = new Map(prev);
        next.set(layerId, {
          status: update.status,
          progress: update.progress ?? 0,
          errorMessage: update.errorMessage ?? null,
        });
        return next;
      });
    }
  }, []);

  const shouldPoll = pending.size > 0 || uploads.size > 0;
  const { data: layers } = useLayers(
    projectId,
    shouldPoll ? POLL_INTERVAL_MS : false,
  );
  // Stage 8.7: derive "already in site plan pool" set from the
  // candidates list. Cheap — one request, one Set construction.
  const { data: candidates } = useSitePlanCandidates(projectId);
  const sitePlanLayerIds = useMemo(
    () => new Set((candidates ?? []).map((c) => c.layer_id)),
    [candidates],
  );

  const { data: project } = useProject(projectId);
  const dispatchExtract = useDispatchExtraction(projectId);
  const addToSitePlan = useAddToSitePlanCandidates(projectId);
  const removeSitePlanCandidate = useRemoveSitePlanCandidate(projectId);
  const qc = useQueryClient();

  const reclassify = useMutation<
    ReclassifyResponseBody,
    Error,
    { layerId: string; hint?: string | null }
  >({
    mutationFn: ({ layerId, hint }) =>
      reclassifyLayer(projectId, layerId, hint ?? null),
    onSuccess: (resp, vars) => {
      setPending((prev) => {
        const next = new Map(prev);
        next.delete(vars.layerId);
        return next;
      });
      setSystemErrors((prev) => {
        const next = new Set(prev);
        if (resp.outcome === "system_error") next.add(vars.layerId);
        else next.delete(vars.layerId);
        return next;
      });
      void qc.invalidateQueries({
        queryKey: projectsQueryKeys.layers(projectId),
      });
    },
    onError: (_e, vars) => {
      setPending((prev) => {
        const next = new Map(prev);
        next.delete(vars.layerId);
        return next;
      });
      setSystemErrors((prev) => {
        const next = new Set(prev);
        next.add(vars.layerId);
        return next;
      });
    },
  });

  useEffect(() => {
    if (pending.size === 0 || !layers) return;
    const now = Date.now();
    let mutated = false;
    const next = new Map(pending);
    for (const [id, info] of pending) {
      const live = layers.find((l) => l.id === id);
      if (!live) {
        next.delete(id);
        mutated = true;
        continue;
      }
      // Done when the classifier has written its result: a PDF flips
      // ``classification`` to a non-null value, a geometry file gets its
      // ``feature_classifications`` populated. Either ends the poll for
      // this layer so the UI settles. (No page refresh — React Query
      // refetch drives the live update.)
      const pdfClassified =
        live.classification !== info.snapshot && live.classification !== null;
      const geometryClassified = live.feature_classifications !== null;
      if (
        pdfClassified ||
        geometryClassified ||
        now - info.startedAt > POLL_TIMEOUT_MS
      ) {
        next.delete(id);
        mutated = true;
      }
    }
    if (mutated) setPending(next);
  }, [layers, pending]);

  const handleAction = (layer: Layer, action: FileAction) => {
    switch (action) {
      // Stage 8.7: smart-routed PDF extraction. All three "Add to X
      // candidates" actions dispatch the same extraction pipeline
      // because the classifier picks the right parser. The action
      // label is the user-visible split; the wire call is the same.
      case "add-to-module-candidates":
      case "add-to-inverter-candidates":
        dispatchExtract.mutate(layer.id);
        return;
      case "add-to-tracker-candidates":
        // No-op: button is rendered disabled with a tooltip. The
        // ``onClick`` would still fire on keyboard click in some
        // browsers — defensively ignore.
        return;
      case "add-to-site-plan-candidates":
        addToSitePlan.mutate({ layer_id: layer.id });
        return;
      case "unlink-from-site-plan": {
        // Symmetric to "Add to site plan candidates" — that action
        // bulk-added every feature in the layer, so unlink removes
        // every candidate row pointing at this layer_id. Fire all
        // deletes in parallel; the hook's onSuccess invalidates the
        // candidates query so the next render flips the row back to
        // ``site-plan-routable`` automatically.
        const matching = (candidates ?? []).filter(
          (c) => c.layer_id === layer.id,
        );
        for (const cand of matching) {
          removeSitePlanCandidate.mutate(cand.id);
        }
        return;
      }
      case "unlink":
        // TODO(Stage 9): component unlink needs a backend lookup of
        // (layer_id → tenant_component_link.id) before it can call
        // ``DELETE /api/projects/{id}/components/{component_id}/link``.
        // The Files tab can't currently reach this state because
        // ``isLinkedComponent`` is hardcoded false in
        // ``computeFileRowActions``; once it's wired to a real query
        // we need this branch to do the deletion.
        console.info("[Files tab] component unlink not yet wired");
        return;
      case "retry-system-error":
      case "reclassify":
        setSystemErrors((prev) => {
          const next = new Set(prev);
          next.delete(layer.id);
          return next;
        });
        setPending((prev) =>
          new Map(prev).set(layer.id, {
            snapshot: layer.classification,
            startedAt: Date.now(),
          }),
        );
        reclassify.mutate({ layerId: layer.id, hint: null });
        return;
      case "reclassify-with-hints":
        setHintForLayer(layer);
        return;
      case "view-in-workflow":
        navigate(`/projects/${layer.project_id}?tab=studies`);
        return;
      case "view-component":
        navigate(`/projects/${layer.project_id}`);
        return;
      default:
        // unlink, unlink-from-site-plan, just-store, delete — not
        // wired in 8.7 (kept in the matrix for forward compat).
        console.info("[Files tab] action not yet wired:", action);
    }
  };

  const onSubmitHint = (hint: string) => {
    if (!hintForLayer) return;
    setPending((prev) =>
      new Map(prev).set(hintForLayer.id, {
        snapshot: hintForLayer.classification,
        startedAt: Date.now(),
      }),
    );
    reclassify.mutate({ layerId: hintForLayer.id, hint });
    setHintForLayer(null);
  };

  // ---- Sort state. Default: Uploaded DESC (newest first). Name asc. ----
  const [sortKey, setSortKey] = useState<SortKey>("uploaded");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  const toggleSort = (key: SortKey) => {
    // Don't nest state setters inside another setter's updater —
    // React strict-mode double-invokes the outer updater, which fired
    // the direction toggle twice and made the column appear "stuck"
    // on its initial direction. Branch off the current state directly.
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "uploaded" ? "desc" : "asc");
    }
  };

  const sortedLayers = useMemo(() => {
    if (!layers) return [];
    const copy = [...layers];
    if (sortKey === "name") {
      copy.sort((a, b) => {
        const an = (a.original_filename || a.name || "").toLowerCase();
        const bn = (b.original_filename || b.name || "").toLowerCase();
        const cmp = an.localeCompare(bn);
        return sortDir === "asc" ? cmp : -cmp;
      });
    } else {
      copy.sort((a, b) => {
        const at = Date.parse(a.created_at) || 0;
        const bt = Date.parse(b.created_at) || 0;
        return sortDir === "asc" ? at - bt : bt - at;
      });
    }
    return copy;
  }, [layers, sortKey, sortDir]);

  const selectedLayer =
    (sortedLayers && selectedLayerId
      ? sortedLayers.find((l) => l.id === selectedLayerId) ?? null
      : null) ?? null;

  const selectedActions = selectedLayer
    ? computeFileRowActions(selectedLayer, {
        isInSitePlanPool: sitePlanLayerIds.has(selectedLayer.id),
        classificationPending: pending.has(selectedLayer.id),
        classificationSystemError: systemErrors.has(selectedLayer.id),
      })
    : null;

  // Stage 8.7-followup: delete is no longer surfaced inside the
  // preview panel. The destructive action moved to a per-row trashcan
  // icon in the Uploaded column (see the table render below). We
  // still compute the non-destructive overflow for the panel.
  const overflowNonDestructive =
    selectedActions?.overflow.filter((a) => a !== "delete") ?? [];
  const overflowDestructive: FileAction[] = [];

  const handleDelete = (layer: Layer) => {
    setDeleteTarget(layer);
  };

  useEffect(() => {
    if (!selectedLayerId) return;
    if (!sortedLayers.find((l) => l.id === selectedLayerId)) {
      setSelectedLayerId(null);
    }
  }, [selectedLayerId, sortedLayers]);

  const layoutStyle: React.CSSProperties = isCompact
    ? { display: "flex", flexDirection: "column", gap: "1.25rem" }
    : {
        display: "grid",
        gridTemplateColumns: "minmax(0, 60fr) minmax(0, 40fr)",
        gap: "1.5rem",
        alignItems: "flex-start",
      };

  // We render the table using <colgroup> so the Name column gets the
  // dominant width and Kind compresses. Without an explicit colgroup
  // the browser would balance widths to text content — long filenames
  // would push Uploaded off the right edge before they got truncated.
  return (
    <section data-testid="files-tab">
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "0.75rem",
        }}
      >
        <button
          type="button"
          data-testid="upload-file-button"
          onClick={() => setShowUpload(true)}
          style={primaryBtn}
        >
          Upload files
        </button>
      </div>

      <div data-testid="files-layout" style={layoutStyle}>
        <div style={{ minWidth: 0 }}>
          <table
            data-testid="files-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              {/* Stage 8.7 — Name takes the leftover space; Kind and
                  Uploaded are pinned in pixels. With ``tableLayout:
                  fixed`` the pixel columns are reserved first, then
                  Name flexes into whatever remains. */}
              <col data-testid="files-col-name" />
              <col style={{ width: 200 }} data-testid="files-col-kind" />
              <col style={{ width: 160 }} data-testid="files-col-uploaded" />
            </colgroup>
            <thead>
              <tr>
                <th
                  style={{ ...headerCellStyle, cursor: "pointer" }}
                  data-testid="files-sort-name"
                  onClick={() => toggleSort("name")}
                  title="Click to toggle sort"
                  aria-sort={
                    sortKey === "name"
                      ? sortDir === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  Name
                  <SortArrow
                    active={sortKey === "name"}
                    direction={sortDir}
                  />
                </th>
                <th style={headerCellStyle}>Kind</th>
                <th
                  style={{ ...headerCellStyle, cursor: "pointer" }}
                  data-testid="files-sort-uploaded"
                  onClick={() => toggleSort("uploaded")}
                  title="Click to toggle sort"
                  aria-sort={
                    sortKey === "uploaded"
                      ? sortDir === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  Uploaded
                  <SortArrow
                    active={sortKey === "uploaded"}
                    direction={sortDir}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedLayers.length === 0 && (
                <tr>
                  <td
                    data-testid="files-empty-row"
                    colSpan={3}
                    style={{
                      ...cellStyle,
                      color: "#94a3b8",
                      textAlign: "center",
                      padding: "1.25rem",
                    }}
                  >
                    No files uploaded yet. Use the Upload files button
                    to add KMZ, GeoJSON, or PDF datasheets.
                  </td>
                </tr>
              )}
              {sortedLayers.map((layer) => {
                const upload = uploads.get(layer.id);
                const isSelected = selectedLayerId === layer.id;
                const isHovered = hoveredLayerId === layer.id;
                const fullFilename = layer.original_filename || layer.name;
                const displayedName = middleEllipsis(fullFilename);
                // Delete is allowed unless the row is mid-classification
                // (matrix: classification-pending has empty overflow).
                const rowActions = computeFileRowActions(layer, {
                  isInSitePlanPool: sitePlanLayerIds.has(layer.id),
                  classificationPending: pending.has(layer.id),
                  classificationSystemError: systemErrors.has(layer.id),
                });
                const canDelete = rowActions.overflow.includes("delete");
                // Selection wins over hover for the background colour so
                // the selected row stays clearly marked when the cursor
                // sits on it.
                const rowBg = isSelected
                  ? "rgba(99, 91, 255, 0.10)"
                  : isHovered
                    ? "rgba(99, 91, 255, 0.04)"
                    : "transparent";
                return (
                  <tr
                    key={layer.id}
                    data-testid={`file-row-${layer.id}`}
                    aria-selected={isSelected}
                    onClick={() =>
                      setSelectedLayerId(isSelected ? null : layer.id)
                    }
                    onMouseEnter={() => setHoveredLayerId(layer.id)}
                    onMouseLeave={() =>
                      setHoveredLayerId((id) =>
                        id === layer.id ? null : id,
                      )
                    }
                    style={{
                      cursor: "pointer",
                      background: rowBg,
                      transition: "background-color 80ms ease",
                    }}
                  >
                    <td
                      style={{
                        ...cellStyle,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "clip",
                      }}
                      title={fullFilename}
                      data-testid={`file-row-name-${layer.id}`}
                    >
                      {displayedName}
                    </td>
                    <td style={cellStyle}>
                      <KindCell
                        layer={layer}
                        upload={upload}
                        pendingClassify={pending.has(layer.id)}
                        classifySystemError={systemErrors.has(layer.id)}
                      />
                    </td>
                    <td
                      style={{
                        ...cellStyle,
                        whiteSpace: "nowrap",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 6,
                      }}
                      data-testid={`file-row-uploaded-${layer.id}`}
                    >
                      <span
                        title={new Date(layer.created_at).toLocaleString()}
                      >
                        {formatUploadedAt(layer.created_at)}
                      </span>
                      {canDelete && (
                        <button
                          type="button"
                          data-testid={`file-row-delete-${layer.id}`}
                          aria-label={`Delete ${fullFilename}`}
                          title="Delete file"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(layer);
                          }}
                          style={trashBtnStyle}
                        >
                          <TrashIcon />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ minWidth: 0 }}>
          <FilePreviewPanel
            projectId={projectId}
            layer={selectedLayer}
            primary={selectedActions?.primary ?? null}
            primaryDisabledReason={selectedActions?.primaryDisabledReason}
            overflow={overflowNonDestructive}
            destructive={overflowDestructive}
            isCompact={isCompact}
            classifying={!!selectedLayer && pending.has(selectedLayer.id)}
            onAction={(action) =>
              selectedLayer && handleAction(selectedLayer, action)
            }
          />
        </div>
      </div>

      {showUpload && (
        <FileUploadModal
          projectId={projectId}
          onClose={() => setShowUpload(false)}
          onFileProgress={onUploadProgress}
        />
      )}
      {hintForLayer && (
        <ReclassifyHintModal
          layer={hintForLayer}
          onCancel={() => setHintForLayer(null)}
          onSubmit={onSubmitHint}
          submitting={reclassify.isPending}
        />
      )}
      {deleteTarget && (
        <DeleteFileModal
          projectId={projectId}
          layer={deleteTarget}
          sitePlanCandidateCount={
            (candidates ?? []).filter((c) => c.layer_id === deleteTarget.id)
              .length
          }
          isActiveBoundary={project?.boundary_layer_id === deleteTarget.id}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            if (selectedLayerId === deleteTarget.id) setSelectedLayerId(null);
            setDeleteTarget(null);
          }}
        />
      )}
    </section>
  );
}

const headerCellStyle: React.CSSProperties = {
  borderBottom: "1px solid #e5e7eb",
  padding: "0.5rem",
  textAlign: "left",
  fontWeight: 600,
  fontSize: "0.85rem",
  color: "#475569",
};

const cellStyle: React.CSSProperties = {
  borderBottom: "1px solid #f3f4f6",
  padding: "0.6rem 0.5rem",
  textAlign: "left",
  verticalAlign: "middle",
  fontSize: "0.875rem",
};

const inflightBadge: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "#eef2ff",
  color: "#4338ca",
  padding: "0.15rem 0.5rem",
  borderRadius: 999,
  fontSize: "0.75rem",
  fontWeight: 500,
};

const errorBadge: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "#fef2f2",
  color: "#b91c1c",
  padding: "0.15rem 0.5rem",
  borderRadius: 999,
  fontSize: "0.75rem",
  fontWeight: 500,
};

const primaryBtn: React.CSSProperties = {
  background: "#635bff",
  color: "white",
  border: "none",
  padding: "0.55rem 1rem",
  borderRadius: 6,
  cursor: "pointer",
  fontWeight: 500,
};

const trashBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 4,
  borderRadius: 4,
  cursor: "pointer",
  color: "#94a3b8",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "color 100ms ease, background-color 100ms ease",
};
